import { useState, useCallback, useMemo, useEffect } from "react";
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Chip,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    CardContent,
    CardActions,
} from "@mui/material";
import {
    ExpandMore,
    Delete,
    CallSplit,
    Merge as MergeIcon,
    ArrowForward,
    Add,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";

import { GroupTask, MatchFormsData, PageGrouping, PageInfo } from "../../interface/returns";
import { AppDispatch } from "../../configureStore";
import { SET_HAS_CHANGES } from "../../redux/reducer/returns";

interface PageGroupVerifierParams {
    groupTask: GroupTask;
    matchData: MatchFormsData;
    setMatchData: (data: MatchFormsData) => void;
    onPageChange: (page: number) => void;
    onNext: () => void;
}

const PageGroupVerifier = ({
    groupTask,
    matchData,
    setMatchData,
    onPageChange,
    onNext,
}: PageGroupVerifierParams) => {
    const dispatch = useDispatch<AppDispatch>();
    const [expandedTaxYear, setExpandedTaxYear] = useState<string | false>(false);
    const [taxYearsInitialized, setTaxYearsInitialized] = useState(false);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showSplitDialog, setShowSplitDialog] = useState(false);
    const [showMergeDialog, setShowMergeDialog] = useState(false);
    const [selectedGroupForSplit, setSelectedGroupForSplit] = useState<string | null>(null);
    const [selectedGroupsForMerge, setSelectedGroupsForMerge] = useState<string[]>([]);
    const [newFormType, setNewFormType] = useState('');
    const [newTaxYear, setNewTaxYear] = useState('');

    const extractedData = groupTask?.blobs?.json || {};

    // Initialize page groups from extracted data
    useEffect(() => {
        if (Object.keys(matchData.pageGroups).length === 0 && Object.keys(extractedData).length > 0) {
            const newPageGroups: Record<string, PageGrouping> = {};

            Object.keys(extractedData).forEach((taxYear) => {
                const forms = extractedData[taxYear] || [];
                forms.forEach((form: any, index: number) => {
                    const formInstanceId = `${taxYear}-${form.formType}-${index}-${crypto.randomUUID().slice(0, 8)}`;
                    const isStatements = form.formType === 'statements';
                    const isW2 = form.formType === 'form_w2';
                    const is4684 = form.formType === 'form_4684';
                    const pages: PageInfo[] = (form.pages?.flatMap((p: any, pageIdx: number): PageInfo[] => {
                        // Regular forms use extractedData.page, statements use extractedData.originalPage
                        const pageNum = p.extractedData?.originalPage ?? p.extractedData?.page ?? (pageIdx + 1);

                        if (is4684 && p.extractedData?.data) {
                            // Each populated property column and the form's "shell" become their own chips
                            // so the user can split-off individual columns to different entities.
                            const data = p.extractedData.data;
                            const items: PageInfo[] = [];
                            if (data.shellId) {
                                items.push({
                                    page: pageNum,
                                    casualtyShellId: data.shellId,
                                    casualtyLabel: 'Form 4684 (header & totals)',
                                });
                            }
                            const buildChip = (prop: any, sectionLabel: string): PageInfo | null => {
                                if (!prop?.propertyId) return null;
                                const letter = prop.propertyLetter ? ` · Col ${prop.propertyLetter}` : '';
                                const desc = prop.description ? ` · ${prop.description}` : '';
                                const fullLabel = `${sectionLabel}${letter}${desc}`;
                                return {
                                    page: pageNum,
                                    casualtyPropertyId: prop.propertyId,
                                    casualtyLabel: fullLabel.length > 60 ? `${fullLabel.slice(0, 57)}...` : fullLabel,
                                };
                            };
                            for (const prop of (data.sectionA?.properties ?? [])) {
                                const chip = buildChip(prop, 'Section A');
                                if (chip) items.push(chip);
                            }
                            for (const prop of (data.sectionBPartOne?.properties ?? [])) {
                                const chip = buildChip(prop, 'Section B I');
                                if (chip) items.push(chip);
                            }
                            for (const prop of (data.sectionB?.properties ?? [])) {
                                const chip = buildChip(prop, 'Section B');
                                if (chip) items.push(chip);
                            }
                            return items;
                        }

                        const pageInfo: PageInfo = { page: pageNum };
                        if (isStatements && p.extractedData?.data?.[0]) {
                            const stmtData = p.extractedData.data[0];
                            if (stmtData.id) {
                                pageInfo.statementId = stmtData.id;
                            }
                            if (stmtData.title) {
                                pageInfo.title = stmtData.title;
                            }
                        }
                        if (isW2 && p.extractedData?.data?.[0]?.id) {
                            pageInfo.w2Id = p.extractedData.data[0].id;
                        }
                        return [pageInfo];
                    }) ?? []).filter((p: PageInfo) => p.page || p.statementId || p.casualtyShellId || p.casualtyPropertyId);

                    newPageGroups[formInstanceId] = {
                        formInstanceId,
                        formType: form.formType,
                        taxYear,
                        pages,
                        tin: form.tin || undefined,
                        name: form.name || undefined,
                    };
                });
            });

            setMatchData({
                ...matchData,
                pageGroups: newPageGroups,
            });
        }
    }, [extractedData, matchData, setMatchData]);

    // Backfill name/tin from extractedData onto pageGroups loaded from localStorage
    useEffect(() => {
        if (Object.keys(matchData.pageGroups).length === 0 || Object.keys(extractedData).length === 0) return;

        let hasUpdates = false;
        const updatedGroups = { ...matchData.pageGroups };

        Object.keys(updatedGroups).forEach((groupId) => {
            const group = updatedGroups[groupId];
            if (group.name) return; // Already has name, skip

            const forms = extractedData[group.taxYear] || [];
            const match = forms.find((f: any) => f.formType === group.formType && f.name);
            if (match) {
                updatedGroups[groupId] = {
                    ...group,
                    name: match.name,
                    tin: group.tin || match.tin || undefined,
                };
                hasUpdates = true;
            }
        });

        if (hasUpdates) {
            setMatchData({
                ...matchData,
                pageGroups: updatedGroups,
            });
        }
    }, [extractedData, matchData, setMatchData]);

    // Get all tax years from current page groups (not from extractedData)
    const taxYears = useMemo(() => {
        const years = new Set<string>();
        Object.values(matchData.pageGroups).forEach((group) => {
            years.add(group.taxYear);
        });
        return Array.from(years).sort((a, b) => {
            const yearA = parseInt(a, 10);
            const yearB = parseInt(b, 10);
            // Put invalid years at the end
            if (Number.isNaN(yearA)) return 1;
            if (Number.isNaN(yearB)) return -1;
            return yearB - yearA;
        });
    }, [matchData.pageGroups]);

    // Auto-expand first tax year on mount
    useEffect(() => {
        if (!taxYearsInitialized && taxYears.length > 0) {
            setExpandedTaxYear(taxYears[0]);
            setTaxYearsInitialized(true);
        }
    }, [taxYears, taxYearsInitialized]);

    // Check if tax year is valid
    const isTaxYearValid = (year: string): boolean => /^\d{4}$/.test(year);

    // Handle tax year correction for a specific form group
    const handleFormTaxYearCorrection = useCallback((groupId: string, newYear: string) => {
        const group = matchData.pageGroups[groupId];
        if (!group) return;

        const newPageGroups = {
            ...matchData.pageGroups,
            [groupId]: {
                ...group,
                taxYear: newYear,
            },
        };

        setMatchData({
            ...matchData,
            pageGroups: newPageGroups,
        });
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
    }, [matchData, setMatchData, dispatch]);

    // Get page groups for a tax year
    const getGroupsForTaxYear = useCallback((taxYear: string): PageGrouping[] => Object.values(matchData.pageGroups).filter(
        (group) => group.taxYear === taxYear,
    ), [matchData.pageGroups]);

    // Delete a group
    const handleDeleteGroup = useCallback((groupId: string) => {
        const newPageGroups = { ...matchData.pageGroups };
        delete newPageGroups[groupId];
        setMatchData({
            ...matchData,
            pageGroups: newPageGroups,
        });
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
    }, [matchData, setMatchData, dispatch]);

    // Move page/statement to different group
    const handleMoveItem = useCallback((itemToMove: PageInfo, fromGroupId: string | null, toGroupId: string) => {
        const newPageGroups = { ...matchData.pageGroups };

        // Match chips by their unique ID first (statement / w2 / 4684 shell or property),
        // falling back to page number for plain page chips.
        const matchItem = (p: PageInfo) => {
            if (itemToMove.statementId) return p.statementId === itemToMove.statementId;
            if (itemToMove.w2Id) return p.w2Id === itemToMove.w2Id;
            if (itemToMove.casualtyShellId) return p.casualtyShellId === itemToMove.casualtyShellId;
            if (itemToMove.casualtyPropertyId) return p.casualtyPropertyId === itemToMove.casualtyPropertyId;
            return p.page === itemToMove.page;
        };

        // Remove from old group if applicable
        if (fromGroupId && newPageGroups[fromGroupId]) {
            newPageGroups[fromGroupId] = {
                ...newPageGroups[fromGroupId],
                pages: newPageGroups[fromGroupId].pages.filter((p) => !matchItem(p)),
            };
        }

        // Add to new group
        if (newPageGroups[toGroupId]) {
            newPageGroups[toGroupId] = {
                ...newPageGroups[toGroupId],
                pages: [...newPageGroups[toGroupId].pages, itemToMove].sort((a, b) => a.page - b.page),
            };
        }

        setMatchData({
            ...matchData,
            pageGroups: newPageGroups,
        });
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
    }, [matchData, setMatchData, dispatch]);

    // Create new group
    const handleCreateGroup = useCallback(() => {
        const formInstanceId = `${newTaxYear}-${newFormType}-${crypto.randomUUID().slice(0, 8)}`;
        const newPageGroups = {
            ...matchData.pageGroups,
            [formInstanceId]: {
                formInstanceId,
                formType: newFormType,
                taxYear: newTaxYear,
                pages: [],
            },
        };

        setMatchData({
            ...matchData,
            pageGroups: newPageGroups,
        });
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
        setShowCreateDialog(false);
        setNewFormType('');
        setNewTaxYear('');
    }, [matchData, setMatchData, dispatch, newFormType, newTaxYear]);

    // Split group
    const handleSplitGroup = useCallback((groupId: string, splitAtItem: PageInfo) => {
        const group = matchData.pageGroups[groupId];
        if (!group) return;

        // Match split point by chip ID (statement / w2 / 4684 shell or property);
        // fall back to page number for plain page chips.
        const index = group.pages.findIndex((p) => {
            if (splitAtItem.statementId) return p.statementId === splitAtItem.statementId;
            if (splitAtItem.w2Id) return p.w2Id === splitAtItem.w2Id;
            if (splitAtItem.casualtyShellId) return p.casualtyShellId === splitAtItem.casualtyShellId;
            if (splitAtItem.casualtyPropertyId) return p.casualtyPropertyId === splitAtItem.casualtyPropertyId;
            return p.page === splitAtItem.page;
        });
        if (index === -1) return;

        const newGroup1Id = `${group.taxYear}-${group.formType}-${crypto.randomUUID().slice(0, 8)}`;
        const newGroup2Id = `${group.taxYear}-${group.formType}-${crypto.randomUUID().slice(0, 8)}`;

        const newPageGroups = { ...matchData.pageGroups };
        delete newPageGroups[groupId];

        newPageGroups[newGroup1Id] = {
            formInstanceId: newGroup1Id,
            formType: group.formType,
            taxYear: group.taxYear,
            pages: group.pages.slice(0, index),
            tin: group.tin,
            name: group.name,
        };

        newPageGroups[newGroup2Id] = {
            formInstanceId: newGroup2Id,
            formType: group.formType,
            taxYear: group.taxYear,
            pages: group.pages.slice(index),
            tin: group.tin,
            name: group.name,
        };

        setMatchData({
            ...matchData,
            pageGroups: newPageGroups,
        });
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
        setShowSplitDialog(false);
        setSelectedGroupForSplit(null);
    }, [matchData, setMatchData, dispatch]);

    // Toggle group selection for merge
    const handleToggleMergeSelection = useCallback((groupId: string) => {
        setSelectedGroupsForMerge((prev) => {
            if (prev.includes(groupId)) {
                return prev.filter((id) => id !== groupId);
            }
            return [...prev, groupId];
        });
    }, []);

    // Merge selected groups
    const handleMergeGroups = useCallback(() => {
        if (selectedGroupsForMerge.length < 2) return;

        const groupsToMerge = selectedGroupsForMerge.map((id) => matchData.pageGroups[id]).filter(Boolean);
        if (groupsToMerge.length < 2) return;

        // Use the first group's metadata
        const firstGroup = groupsToMerge[0];
        const mergedId = `${firstGroup.taxYear}-${firstGroup.formType}-${crypto.randomUUID().slice(0, 8)}`;

        // Dedupe by chip identity (statement / w2 / 4684 shell or property), falling back to page
        // number for plain page chips. A single PDF page can produce multiple 4684 chips, so we
        // can't dedupe purely by page number.
        const chipKey = (p: PageInfo): string => p.statementId
            ?? p.w2Id
            ?? p.casualtyShellId
            ?? p.casualtyPropertyId
            ?? `page-${p.page}`;
        const seenChips = new Map<string, PageInfo>();
        groupsToMerge.forEach((group) => {
            group.pages.forEach((pageInfo) => {
                const key = chipKey(pageInfo);
                if (!seenChips.has(key)) {
                    seenChips.set(key, pageInfo);
                }
            });
        });
        const sortedPages = Array.from(seenChips.values()).sort((a, b) => a.page - b.page);

        // Create new merged group
        const newPageGroups = { ...matchData.pageGroups };

        // Delete old groups
        selectedGroupsForMerge.forEach((id) => {
            delete newPageGroups[id];
        });

        // Add merged group
        newPageGroups[mergedId] = {
            formInstanceId: mergedId,
            formType: firstGroup.formType,
            taxYear: firstGroup.taxYear,
            pages: sortedPages,
            tin: firstGroup.tin,
            name: firstGroup.name,
        };

        setMatchData({
            ...matchData,
            pageGroups: newPageGroups,
        });
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
        setShowMergeDialog(false);
        setSelectedGroupsForMerge([]);
    }, [selectedGroupsForMerge, matchData, setMatchData, dispatch]);

    // Check if all forms have valid tax years
    const hasInvalidTaxYears = useMemo(() => Object.values(matchData.pageGroups).some(
        (group) => !isTaxYearValid(group.taxYear),
    ), [matchData.pageGroups]);

    // Check if can proceed to next step
    const canProceed = useMemo(() => !hasInvalidTaxYears, [hasInvalidTaxYears]);

    // Render page group
    const renderPageGroup = (group: PageGrouping, inMergeMode: boolean = false) => {
        const isSelected = selectedGroupsForMerge.includes(group.formInstanceId);
        const isInvalidYear = !isTaxYearValid(group.taxYear);

        return (
            <Card
                key={group.formInstanceId}
                variant="outlined"
                onClick={inMergeMode ? () => handleToggleMergeSelection(group.formInstanceId) : undefined}
                sx={{
                    mb: 1.5,
                    borderWidth: 1,
                    borderRadius: 2,
                    borderColor: isSelected ? 'primary.main' : isInvalidYear ? 'warning.main' : 'divider',
                    bgcolor: isSelected ? 'action.selected' : isInvalidYear ? 'warning.lighter' : 'background.paper',
                    cursor: inMergeMode ? 'pointer' : 'default',
                    '&:hover': {
                        borderColor: 'primary.light',
                    },
                }}
            >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: inMergeMode ? 1.5 : 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
                            {group.formType}
                            {group.name && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                                    {group.name}
                                </Typography>
                            )}
                        </Typography>
                        {/* Per-form tax year selector - always editable */}
                        {!inMergeMode && (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                <Select
                                    value={isTaxYearValid(group.taxYear) ? group.taxYear : ''}
                                    displayEmpty
                                    onChange={(e) => handleFormTaxYearCorrection(group.formInstanceId, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    renderValue={(selected) => selected || group.taxYear}
                                    sx={isInvalidYear ? { color: 'warning.main' } : {}}
                                >
                                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                                        <MenuItem key={year} value={year.toString()}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            {(() => {
                                if (group.formType === 'statements') {
                                    return `${group.pages.length} statement${group.pages.length !== 1 ? 's' : ''}`;
                                }
                                if (group.formType === 'form_4684') {
                                    const propCount = group.pages.filter((p) => p.casualtyPropertyId).length;
                                    const shellCount = group.pages.filter((p) => p.casualtyShellId).length;
                                    const parts: string[] = [];
                                    if (shellCount > 0) parts.push('shell');
                                    if (propCount > 0) parts.push(`${propCount} propert${propCount !== 1 ? 'ies' : 'y'}`);
                                    return parts.length > 0 ? parts.join(' + ') : `${group.pages.length} item${group.pages.length !== 1 ? 's' : ''}`;
                                }
                                return `${group.pages.length} page${group.pages.length !== 1 ? 's' : ''}`;
                            })()}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {group.pages.map((pageInfo, idx) => {
                            const isStatement = !!pageInfo.statementId;
                            const isCasualty = !!(pageInfo.casualtyShellId || pageInfo.casualtyPropertyId);
                            let chipLabel: string;
                            if (isCasualty) {
                                chipLabel = pageInfo.casualtyLabel ?? (pageInfo.casualtyShellId ? 'Form 4684 (header)' : `Pg ${pageInfo.page} Property`);
                            } else if (isStatement) {
                                if (pageInfo.title) {
                                    chipLabel = pageInfo.title.length > 30
                                        ? `${pageInfo.title.slice(0, 27)}...`
                                        : pageInfo.title;
                                } else {
                                    chipLabel = `Pg ${pageInfo.page} Stmt ${idx + 1}`;
                                }
                            } else {
                                chipLabel = `Page ${pageInfo.page}`;
                            }
                            const chipKey = pageInfo.casualtyShellId
                                || pageInfo.casualtyPropertyId
                                || pageInfo.statementId
                                || pageInfo.w2Id
                                || `page-${pageInfo.page}-${idx}`;

                            return (
                                <Tooltip key={chipKey} title={pageInfo.title || chipLabel} enterDelay={500} enterNextDelay={500}>
                                    <Chip
                                        label={chipLabel}
                                        size="small"
                                        onClick={(e) => {
                                            if (!inMergeMode) {
                                                e.stopPropagation();
                                                onPageChange(pageInfo.page);
                                            }
                                        }}
                                        onDelete={!inMergeMode ? () => handleMoveItem(pageInfo, group.formInstanceId, '') : undefined}
                                        clickable={!inMergeMode}
                                    />
                                </Tooltip>
                            );
                        })}
                    </Box>
                </CardContent>
                {!inMergeMode && (
                    <CardActions sx={{ px: 1.5, pt: 0, pb: 1 }}>
                        <Tooltip title="Split group">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setSelectedGroupForSplit(group.formInstanceId);
                                        setShowSplitDialog(true);
                                    }}
                                    disabled={group.pages.length < 2}
                                >
                                    <CallSplit fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Delete group">
                            <IconButton size="small" onClick={() => handleDeleteGroup(group.formInstanceId)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </CardActions>
                )}
            </Card>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                    Review and adjust which pages belong to each form
                </Typography>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {taxYears.map((taxYear) => {
                    const isValid = isTaxYearValid(taxYear);
                    const groups = getGroupsForTaxYear(taxYear);

                    return (
                        <Accordion
                            key={taxYear}
                            expanded={expandedTaxYear === taxYear}
                            onChange={(_, isExpanded) => setExpandedTaxYear(isExpanded ? taxYear : false)}
                            sx={{ mb: 1 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        Tax Year: {taxYear}
                                    </Typography>
                                    {!isValid && (
                                        <Chip label="Needs Correction" size="small" color="warning" />
                                    )}
                                    <Chip label={`${groups.length} forms`} size="small" />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {!isValid && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Select the correct tax year for each form below
                                    </Typography>
                                )}
                                {groups.map((group) => renderPageGroup(group, showMergeDialog))}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}

            </Box>

            {/* Actions */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'center' }}>
                {showMergeDialog ? (
                    <>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setShowMergeDialog(false);
                                setSelectedGroupsForMerge([]);
                            }}
                        >
                            Cancel Merge
                        </Button>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {selectedGroupsForMerge.length} groups selected
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<MergeIcon />}
                            onClick={handleMergeGroups}
                            disabled={selectedGroupsForMerge.length < 2}
                        >
                            Merge Selected
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            startIcon={<Add />}
                            variant="outlined"
                            onClick={() => setShowCreateDialog(true)}
                        >
                            New Group
                        </Button>
                        <Button
                            startIcon={<MergeIcon />}
                            variant="outlined"
                            onClick={() => setShowMergeDialog(true)}
                        >
                            Merge Groups
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Tooltip
                            title={!canProceed ? "Correct all invalid tax years to proceed" : ""}
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForward />}
                                    onClick={onNext}
                                    disabled={!canProceed}
                                >
                                    Next: Assign Forms
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}
            </Box>

            {/* Create group dialog */}
            <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)}>
                <DialogTitle>Create Form Group</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Form Type"
                        fullWidth
                        value={newFormType}
                        onChange={(e) => setNewFormType(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Tax Year"
                        fullWidth
                        value={newTaxYear}
                        onChange={(e) => setNewTaxYear(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateGroup} disabled={!newFormType || !newTaxYear}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Split group dialog */}
            <Dialog open={showSplitDialog} onClose={() => setShowSplitDialog(false)}>
                <DialogTitle>Split Form Group</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Select where to split. Items before will stay in one group, items from this point will create a new group.
                    </Typography>
                    {selectedGroupForSplit && matchData.pageGroups[selectedGroupForSplit] && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {matchData.pageGroups[selectedGroupForSplit].pages.slice(1).map((pageInfo, idx) => {
                                const isStatement = !!pageInfo.statementId;
                                const isCasualty = !!(pageInfo.casualtyShellId || pageInfo.casualtyPropertyId);
                                let splitLabel: string;
                                if (isCasualty) {
                                    const label = pageInfo.casualtyLabel ?? 'Form 4684 chip';
                                    splitLabel = `Split at: ${label.length > 40 ? `${label.slice(0, 37)}...` : label}`;
                                } else if (isStatement) {
                                    splitLabel = pageInfo.title
                                        ? `Split at: ${pageInfo.title.length > 40 ? `${pageInfo.title.slice(0, 37)}...` : pageInfo.title}`
                                        : `Split at Pg ${pageInfo.page} Stmt ${idx + 2}`;
                                } else {
                                    splitLabel = `Split at Page ${pageInfo.page}`;
                                }
                                const splitKey = pageInfo.casualtyShellId
                                    || pageInfo.casualtyPropertyId
                                    || pageInfo.statementId
                                    || pageInfo.w2Id
                                    || `page-${pageInfo.page}-${idx}`;
                                return (
                                    <Button
                                        key={splitKey}
                                        variant="outlined"
                                        onClick={() => handleSplitGroup(selectedGroupForSplit, pageInfo)}
                                        fullWidth
                                    >
                                        {splitLabel}
                                    </Button>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSplitDialog(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PageGroupVerifier;
